import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericAutoComplete from "./MyAutoComplete";
import {
  fetchClientsFournisseurs,
  clearSelectedClientFournisseur,
  setSelectedClientFournisseur,
} from "../../store/clientFournisseurSlice";
import { TiersType } from "../../services/Enums";
import {
  setFieldValue,
  setFieldError,
  resetFieldState,
} from "../../store/formValidationSlice";

const ClientFournisseurAutoComplete = ({
  required,
  reset,
  type,
  fieldName = "clientFournisseur",
  error
}) => {
  const dispatch = useDispatch();

  // Select necessary state from Redux
  const { clientsFournisseursData, loading, selectedClientFournisseur } = useSelector(
    (state) => state.clientFournisseur
  );
  const { selectedSale } = useSelector((state) => state.sale);
  const { selectedMouvement } = useSelector((state) => state.mouvementTypes);
  
  const queryCache = useRef({});
  const mouvementType = selectedSale ? selectedSale.mouvementType : selectedMouvement?._id;

  // Determine the resolved type
  const resolvedType = useMemo(() => {
    if (type) return type;
    if (!mouvementType) return TiersType.CLIENT;
    switch (mouvementType) {
      case "676356aa76fb12de5a988f77": // fact client
      case "676356aa76fb12de5a988f79": // proforma
      case "67a15a4cafdb89c59074f89c": // commande client
        return TiersType.CLIENT;
      case "676356aa76fb12de5a988f78": // fact fournisseur
      case "676356aa76fb12de5a988f7a": // commande fournisseur
        return TiersType.FOURNISSEUR;
      default:
        return TiersType.CLIENT;
    }
  }, [type, mouvementType]);
  
  const shouldDisplayAutocomplete = useMemo(() => {
    if (!mouvementType) return true;
    switch (mouvementType) {
      case "676356aa76fb12de5a988f75": //Entree de stock
      case "676356aa76fb12de5a988f76": //Sortie de stock
      case "67f30a5fb65d87704511728f": //transfert sortant
      case "67f5010e985dcb0f516e6be7": //transfert entrant
        return false;
      default:
        dispatch(setFieldError({ field: fieldName, error: `${fieldName} is required.` }));
        return true;
    }
  }, [mouvementType, dispatch, fieldName]);

  // Reset the state when `reset` is true
  useEffect(() => {
    if (reset) {
      dispatch(clearSelectedClientFournisseur());
      dispatch(resetFieldState({ field: fieldName }));
    }
  }, [reset, dispatch, fieldName]);

  // Fetch clients/fournisseurs based on type
  useEffect(() => {
    dispatch(fetchClientsFournisseurs({ type: resolvedType }));
    return () => {
      dispatch(clearSelectedClientFournisseur());
      dispatch(resetFieldState({ field: fieldName }));
    };
  }, [dispatch, resolvedType,fieldName]);

  // Preselect clientFournisseur if it exists in selectedSale
  useEffect(() => {
    if (selectedSale?.clientFournisseur?.name) {
      dispatch(setSelectedClientFournisseur(selectedSale.clientFournisseur));
      dispatch(
        setFieldValue({
          field: fieldName,
          value: selectedSale.clientFournisseur,
        })
      );
    }
  }, [selectedSale, dispatch, fieldName]);

  // Fetch suggestions dynamically, with memoization for caching
  const fetchCF = useCallback(
    (searchValue) => {
      if (!searchValue.trim() || queryCache.current[searchValue]) return;

      const payload = {
        name: searchValue,
        type: resolvedType,
      };
      dispatch(fetchClientsFournisseurs(payload));
      queryCache.current[searchValue] = true;
    },
    [dispatch, resolvedType]
  );

  // Handle selection and validation
  const handleSelect = (selectedCF) => {
    dispatch(setSelectedClientFournisseur(selectedCF));
    dispatch(setFieldValue({ field: fieldName, value: selectedCF }));

    if (shouldDisplayAutocomplete && !selectedCF) {
      if (required && !selectedCF) {
        dispatch(setFieldError({ field: fieldName, error: `${fieldName} is required.` }));
      }
    } else {
      dispatch(setFieldError({ field: fieldName, error: "" }));
    }
  };

  // Memoized getOptionLabel function
  const getOptionLabel = useCallback(
    (option) => option?.name || "",
    []
  );

  return (
    <>
      {shouldDisplayAutocomplete && (
        <GenericAutoComplete
          label={resolvedType === TiersType.FOURNISSEUR ? "Nom Fournisseur" : "Nom Client"}
          onSelect={handleSelect}
          fetchData={fetchCF}
          options={clientsFournisseursData}
          loading={loading}
          getOptionLabel={getOptionLabel}
          debounceTime={800}
          required={required}
          defaultValue={reset ? null : selectedClientFournisseur}
        />
      )}
    {error && <p className="error-message">{error}</p>}
    </>

  );
};

export default ClientFournisseurAutoComplete;
